import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@api/client';
import { resolveHeaderApiError, type HeaderApiError } from './headerApiError';

export interface Notification {
  id: number;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
  created_at: string;
  is_read: boolean;
  url?: string;
}

interface NotificationsResponse {
  notifications: Notification[];
  unread_count: number;
}

// API функции
async function fetchNotifications(): Promise<NotificationsResponse> {
  const { data } = await apiClient.get<NotificationsResponse>('/notifications', {
    params: { limit: 5 },
  });
  return data;
}

async function markAsRead(notificationId: number): Promise<void> {
  await apiClient.put(`/notifications/${notificationId}/read`);
}

export function useNotifications() {
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['notifications'],
    queryFn: fetchNotifications,
    refetchInterval: 30000, // Обновлять каждые 30 секунд
    retry: false,
  });

  const headerError: HeaderApiError | null = resolveHeaderApiError(error);

  const markReadMutation = useMutation({
    mutationFn: markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  return {
    notifications: data?.notifications || [],
    unreadCount: data?.unread_count || 0,
    isLoading,
    isError,
    error: headerError,
    refetch,
    markAsRead: (id: number) => markReadMutation.mutate(id),
  };
}

